/**
 * RichTextTitleEditor - A rich text editor for video titles
 * 
 * Allows users to:
 * - Color individual words differently
 * - Control line breaks
 * - Apply bold/italic formatting
 * - Set text alignment
 * - Add emojis
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Bold, Italic, AlignLeft, AlignCenter, AlignRight,
  Undo, Redo, Type, ChevronDown
} from 'lucide-react';
import './RichTextTitleEditor.css';

// Common emoji categories for quick access
const QUICK_EMOJIS = ['😀', '🔥', '⭐', '💯', '🎯', '👑', '💪', '🚀', '❤️', '😱', '🤯', '😂', '🎉', '✨', '🏆', '💥'];

// Font size presets
const FONT_SIZES = [24, 32, 40, 48, 56, 64, 72, 80, 96, 120];

// Preset color palette
const COLOR_PALETTE = [
  '#FFFFFF', '#000000', '#FF0000', '#00FF00', '#0000FF',
  '#eeba2c', '#d4a322', '#fcd34d', '#f0c445', '#FF6B6B',
  '#4ECDC4', '#FFE66D', '#FF8C42', '#95E1D3', '#F38181',
];

export const RichTextTitleEditor = ({ 
  value, 
  onChange,
  titleStyle,
  onStyleChange,
  placeholder = "Enter your title..."
}) => {
  const editorRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [currentTextColor, setCurrentTextColor] = useState('#FFFFFF');
  const [currentBgColor, setCurrentBgColor] = useState('transparent');
  const [currentFontSize, setCurrentFontSize] = useState(48);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);

  // Initialize editor with value
  useEffect(() => {
    if (editorRef.current && value && editorRef.current.innerHTML === '') {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  // Save to history for undo/redo
  const saveToHistory = useCallback(() => {
    if (!editorRef.current) return;
    
    const currentContent = editorRef.current.innerHTML;
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(currentContent);
    
    // Limit history size
    if (newHistory.length > 50) {
      newHistory.shift();
    }
    
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (!editorRef.current) return;
    
    const html = editorRef.current.innerHTML;
    onChange(html);
    
    // Update active formatting states
    updateFormattingState();
  }, [onChange]);

  // Update formatting state based on current selection
  const updateFormattingState = () => {
    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));
  };

  // Execute formatting command
  const execCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
    saveToHistory();
  };

  // Toggle bold
  const toggleBold = () => {
    execCommand('bold');
    setIsBold(!isBold);
  };

  // Toggle italic
  const toggleItalic = () => {
    execCommand('italic');
    setIsItalic(!isItalic);
  };

  // Apply text color to selection
  const applyTextColor = (color) => {
    setCurrentTextColor(color);
    execCommand('foreColor', color);
    setShowTextColorPicker(false);
  };

  // Apply background color to selection
  const applyBgColor = (color) => {
    setCurrentBgColor(color);
    if (color === 'transparent') {
      execCommand('removeFormat');
    } else {
      execCommand('hiliteColor', color);
    }
    setShowBgColorPicker(false);
  };

  // Apply font size to selection
  const applyFontSize = (size) => {
    setCurrentFontSize(size);
    
    // Create a span with inline style for precise font size control
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      
      if (selectedText) {
        const span = document.createElement('span');
        span.style.fontSize = `${size}px`;
        span.textContent = selectedText;
        
        range.deleteContents();
        range.insertNode(span);
        
        // Move cursor after inserted span
        range.setStartAfter(span);
        range.setEndAfter(span);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    
    handleInput();
    saveToHistory();
    setShowFontSizeDropdown(false);
  };

  // Insert emoji at cursor
  const insertEmoji = (emoji) => {
    editorRef.current?.focus();
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const textNode = document.createTextNode(emoji);
      range.deleteContents();
      range.insertNode(textNode);
      
      // Move cursor after emoji
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    handleInput();
    saveToHistory();
    setShowEmojiPicker(false);
  };

  // Set text alignment
  const setAlignment = (align) => {
    if (onStyleChange) {
      onStyleChange('textAlign', align);
    }
    // Also apply to editor for visual feedback
    if (editorRef.current) {
      editorRef.current.style.textAlign = align;
    }
  };

  // Undo
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      if (editorRef.current) {
        editorRef.current.innerHTML = history[newIndex];
        onChange(history[newIndex]);
      }
    }
  };

  // Redo
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      if (editorRef.current) {
        editorRef.current.innerHTML = history[newIndex];
        onChange(history[newIndex]);
      }
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.metaKey || e.ctrlKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          toggleBold();
          break;
        case 'i':
          e.preventDefault();
          toggleItalic();
          break;
        case 'z':
          e.preventDefault();
          if (e.shiftKey) {
            handleRedo();
          } else {
            handleUndo();
          }
          break;
        default:
          break;
      }
    }
    
    // Save to history on Enter
    if (e.key === 'Enter') {
      setTimeout(saveToHistory, 100);
    }
  };

  // Handle blur - save to history
  const handleBlur = () => {
    saveToHistory();
  };

  // Handle focus - close dropdowns
  const handleFocus = () => {
    updateFormattingState();
  };

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.toolbar-dropdown')) {
        setShowEmojiPicker(false);
        setShowFontSizeDropdown(false);
        setShowTextColorPicker(false);
        setShowBgColorPicker(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="rich-text-editor">
      <div className="editor-label">
        <span>Title</span>
        <span className="editor-hint">Appears at the top of the video</span>
      </div>
      
      {/* Toolbar */}
      <div className="editor-toolbar">
        {/* Emoji Picker */}
        <div className="toolbar-dropdown">
          <button
            type="button"
            className="toolbar-btn emoji-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowEmojiPicker(!showEmojiPicker);
              setShowFontSizeDropdown(false);
              setShowTextColorPicker(false);
              setShowBgColorPicker(false);
            }}
            title="Insert Emoji"
          >
            😀
          </button>
          {showEmojiPicker && (
            <div className="dropdown-menu emoji-menu">
              <div className="emoji-grid">
                {QUICK_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="emoji-option"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Font Size Dropdown */}
        <div className="toolbar-dropdown">
          <button
            type="button"
            className="toolbar-btn font-size-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowFontSizeDropdown(!showFontSizeDropdown);
              setShowEmojiPicker(false);
              setShowTextColorPicker(false);
              setShowBgColorPicker(false);
            }}
            title="Font Size"
          >
            <Type size={16} />
            <ChevronDown size={12} />
          </button>
          {showFontSizeDropdown && (
            <div className="dropdown-menu font-size-menu">
              {FONT_SIZES.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={`font-size-option ${size === currentFontSize ? 'active' : ''}`}
                  onClick={() => applyFontSize(size)}
                >
                  {size}px
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Bold */}
        <button
          type="button"
          className={`toolbar-btn ${isBold ? 'active' : ''}`}
          onClick={toggleBold}
          title="Bold (⌘B)"
        >
          <Bold size={16} />
        </button>

        {/* Italic */}
        <button
          type="button"
          className={`toolbar-btn ${isItalic ? 'active' : ''}`}
          onClick={toggleItalic}
          title="Italic (⌘I)"
        >
          <Italic size={16} />
        </button>

        {/* Text Color */}
        <div className="toolbar-dropdown">
          <button
            type="button"
            className="toolbar-btn color-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowTextColorPicker(!showTextColorPicker);
              setShowEmojiPicker(false);
              setShowFontSizeDropdown(false);
              setShowBgColorPicker(false);
            }}
            title="Text Color"
            style={{ '--current-color': currentTextColor }}
          >
            <span className="color-icon">A</span>
            <span className="color-bar" style={{ backgroundColor: currentTextColor }} />
          </button>
          {showTextColorPicker && (
            <div className="dropdown-menu color-menu">
              <div className="color-grid">
                {COLOR_PALETTE.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`color-option ${color === currentTextColor ? 'active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => applyTextColor(color)}
                    title={color}
                  />
                ))}
              </div>
              <div className="custom-color-row">
                <label>Custom:</label>
                <input
                  type="color"
                  value={currentTextColor}
                  onChange={(e) => applyTextColor(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Background/Highlight Color */}
        <div className="toolbar-dropdown">
          <button
            type="button"
            className="toolbar-btn bg-color-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowBgColorPicker(!showBgColorPicker);
              setShowEmojiPicker(false);
              setShowFontSizeDropdown(false);
              setShowTextColorPicker(false);
            }}
            title="Highlight Color"
          >
            <span className="bg-color-icon" style={{ backgroundColor: currentBgColor === 'transparent' ? '#333' : currentBgColor }}>A</span>
            <ChevronDown size={12} />
          </button>
          {showBgColorPicker && (
            <div className="dropdown-menu color-menu">
              <button
                type="button"
                className="color-option transparent-option"
                onClick={() => applyBgColor('transparent')}
              >
                None
              </button>
              <div className="color-grid">
                {COLOR_PALETTE.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`color-option ${color === currentBgColor ? 'active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => applyBgColor(color)}
                    title={color}
                  />
                ))}
              </div>
              <div className="custom-color-row">
                <label>Custom:</label>
                <input
                  type="color"
                  value={currentBgColor === 'transparent' ? '#FFCC00' : currentBgColor}
                  onChange={(e) => applyBgColor(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="toolbar-divider" />

        {/* Alignment */}
        <button
          type="button"
          className={`toolbar-btn ${titleStyle?.textAlign === 'left' ? 'active' : ''}`}
          onClick={() => setAlignment('left')}
          title="Align Left"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          className={`toolbar-btn ${!titleStyle?.textAlign || titleStyle?.textAlign === 'center' ? 'active' : ''}`}
          onClick={() => setAlignment('center')}
          title="Align Center"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          className={`toolbar-btn ${titleStyle?.textAlign === 'right' ? 'active' : ''}`}
          onClick={() => setAlignment('right')}
          title="Align Right"
        >
          <AlignRight size={16} />
        </button>

        {/* Divider */}
        <div className="toolbar-divider" />

        {/* Undo/Redo */}
        <button
          type="button"
          className="toolbar-btn"
          onClick={handleUndo}
          disabled={historyIndex <= 0}
          title="Undo (⌘Z)"
        >
          <Undo size={16} />
        </button>
        <button
          type="button"
          className="toolbar-btn"
          onClick={handleRedo}
          disabled={historyIndex >= history.length - 1}
          title="Redo (⌘⇧Z)"
        >
          <Redo size={16} />
        </button>
      </div>

      {/* Editor Content */}
      <div
        ref={editorRef}
        className="editor-content"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        data-placeholder={placeholder}
        style={{
          textAlign: titleStyle?.textAlign || 'center',
          fontFamily: titleStyle?.font || 'Archivo Black',
        }}
      />

      {/* Helper Text */}
      <div className="editor-help">
        <span>Tip: Select text to change its color or style</span>
      </div>
    </div>
  );
};

export default RichTextTitleEditor;
